/**
 * RentCalculator — PUBLIC lead-magnet page at /tools/rent-calculator.
 *
 * Why it exists: in the July 2026 Google Ads flight the ONLY search term
 * that converted was "rtb rent calculator". People searching that are
 * landlords with a live rent review in front of them — exactly the
 * product's buyer — but the old flow dropped them on a generic landing
 * page. This page answers the query directly, then converts on the one
 * thing a calculator can't do for them: produce the actual RTB notice.
 *
 * Constraints this file works under:
 *  - NO AUTH. It renders for a logged-out cold click, so it must not
 *    import anything that touches Firestore or auth. All of the maths
 *    lives in ../../utils/rentCap.js, which is deliberately dependency-
 *    free. ExitIntentFeedback does touch Firestore, but only inside its
 *    own submit handler, and it is already used this way on Home and
 *    LandlordsIreland.
 *  - Marketing look: same dark theme, nav and CTA treatment as
 *    LandlordsIreland.js, so paid traffic sees one consistent brand.
 *  - SEO: no react-helmet in this project — LandlordsIreland sets
 *    document.title in a useEffect, so this page does the same, plus a
 *    managed meta description and a JSON-LD FAQPage block.
 *
 * The legal rules and their sources are documented in rentCap.js. This
 * file renders them; it does not decide them.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Container, Grid, Paper, Stack, TextField,
  FormControlLabel, Checkbox, Alert, Divider, Link as MuiLink,
  ThemeProvider, createTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventIcon from '@mui/icons-material/Event';
import { useNavigate } from 'react-router-dom';
import ExitIntentFeedback from '../../components/ExitIntentFeedback';
import usePageMeta from '../../utils/pageMeta';
import { trackEvent } from '../../utils/analytics';
import {
  calculateRentReview,
  todayISO,
  formatDateIE,
  NOTICE_CHECKLIST,
  NOTICE_DAYS,
  RTB_COPY_DAYS,
  ANNUAL_CAP_PERCENT,
  DEFAULT_CPI_PERCENT,
  DEFAULT_CPI_PERIOD,
  DEFAULT_CPI_SOURCE_URL,
} from '../../utils/rentCap';

const RED = '#E63946';
// Solid-fill variant of the brand red. White text on #E63946 is 4.17:1,
// just under WCAG AA; #C8303C is 5.32:1 and visually the same colour.
// RED stays the accent (icons, links, borders on the dark background,
// where it measures 4.53:1); RED_SOLID is for anything with white text
// sitting on top of it.
const RED_SOLID = '#C8303C';
const BG = '#0F1117';
const BG_ALT = '#0A0C11';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: RED_SOLID },
    background: { default: BG, paper: BG },
    text: { primary: '#FFFFFF', secondary: 'rgba(255,255,255,0.65)' },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
});

const PAGE_TITLE = 'RTB Rent Increase Calculator (Ireland, 2026) | NestHub';
const PAGE_DESC =
  'Free RTB rent increase calculator for Irish landlords. Work out your maximum rent '
  + 'under the 2026 rent cap, your earliest review date and your notice date.';

const FAQS = [
  {
    q: 'How much can I increase the rent in Ireland in 2026?',
    a: 'For most tenancies, rent can rise by no more than 2% per year or the rate of inflation '
      + 'measured by the Consumer Price Index (CPI), whichever is lower. Because CPI is currently '
      + 'running above 2%, the 2% ceiling is what binds for most landlords. The exception is new '
      + 'apartments in developments whose commencement notice is dated on or after 10 June 2025 — '
      + 'those are capped by CPI alone, with no 2% ceiling.',
  },
  {
    q: 'Do Rent Pressure Zones still exist?',
    a: 'No. Rent Pressure Zones were extended nationwide on 20 June 2025 and then abolished '
      + 'altogether on 28 February 2026. Since 1 March 2026 a single national rent control regime '
      + 'applies to every private tenancy in the State, so it no longer matters whether your '
      + 'property sits in a designated zone. One transitional rule survives: in areas that only '
      + 'became a Rent Pressure Zone in the previous two years, rent cannot be reviewed until 24 '
      + 'months after it was last set.',
  },
  {
    q: 'How often can I review the rent?',
    a: 'Once every 12 months, counted from the date the rent was last set or last reviewed — not '
      + 'from the tenancy anniversary. In areas that became a Rent Pressure Zone within the '
      + 'previous two years the interval is 24 months instead. Reviewing early makes the notice '
      + 'invalid, so the date the rent was last set is the single most important input here.',
  },
  {
    q: 'How much notice do I have to give for a rent increase?',
    a: 'At least 90 days before the new rent takes effect, using the RTB’s prescribed Notice of '
      + 'Rent Review form. A letter of your own will not do. You must also serve a copy on the RTB '
      + 'within 7 days of serving the tenant — if you skip that step the notice is invalid even if '
      + 'the amount and the timing are both correct.',
  },
  {
    q: 'Can I reset the rent to market rate between tenancies?',
    a: 'It depends on when the tenancy began. For a tenancy created on or after 1 March 2026 the '
      + 'rent may be reset to market rate at the end of the six-year Tenancy of Minimum Duration '
      + '(three years for student-specific accommodation), and at the start of a genuinely new '
      + 'tenancy, subject to restrictions where the previous tenancy ended by a no-fault '
      + 'termination. For a tenancy that began before 1 March 2026 the rent cannot be reset to '
      + 'market rate while that tenancy continues. A property that has not been let for at least '
      + 'two years, or has been substantially refurbished, may also be re-set — check the exact '
      + 'conditions with the RTB before relying on any of these.',
  },
];

// JSON-LD so the FAQ can win a rich result on the exact query this page
// targets. Built from the same FAQS array that renders on the page —
// Google requires the markup to match the visible content.
const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

// Shipped alongside the FAQ: a WebApplication entity (this is a free tool,
// and Google treats a priced SoftwareApplication differently from a free
// calculator) plus the breadcrumb that places it under /tools.
const PAGE_JSONLD = [
  FAQ_JSONLD,
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'RTB Rent Increase Calculator',
    url: 'https://nesthub.ie/tools/rent-calculator',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    browserRequirements: 'Requires JavaScript',
    description: PAGE_DESC,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    provider: { '@type': 'Organization', name: 'NestHub', url: 'https://nesthub.ie/' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nesthub.ie/' },
      { '@type': 'ListItem', position: 2, name: 'Rent calculator', item: 'https://nesthub.ie/tools/rent-calculator' },
    ],
  },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.05)' },
  // 44px touch target (WCAG 2.5.8); the default small field is 40px.
  '& .MuiOutlinedInput-input': { paddingTop: '13px', paddingBottom: '13px' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#fff', borderWidth: 2 },
  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.6)' },
};

const euro = (n) => `€${Number(n).toLocaleString('en-IE', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

export default function RentCalculator() {
  const navigate = useNavigate();

  const [currentRent, setCurrentRent] = useState('');
  const [lastSetDate, setLastSetDate] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [tenancyStartDate, setTenancyStartDate] = useState('');
  // Default yes: since 1 March 2026 the cap is national, so "is it
  // covered?" is yes for essentially every private tenancy. The checkbox
  // stays so someone outside the regime (licence, Airbnb, own-home
  // letting) isn't given a number that doesn't apply to them.
  const [isCovered, setIsCovered] = useState(true);
  const [recentRpzArea, setRecentRpzArea] = useState(false);
  const [isNewBuildExempt, setIsNewBuildExempt] = useState(false);
  const [cpiPercent, setCpiPercent] = useState(String(DEFAULT_CPI_PERCENT));
  const [touched, setTouched] = useState(false);

  // SEO. This page's inline head-management effect was extracted into
  // usePageMeta (src/utils/pageMeta.js) so all six public pages share one
  // implementation; the behaviour is unchanged apart from also setting the
  // OG/Twitter tags, which this page previously inherited from the
  // homepage's static block in index.html.
  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESC,
    canonical: '/tools/rent-calculator',
    jsonLd: PAGE_JSONLD,
  });

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = BG;
    return () => { document.body.style.backgroundColor = prevBg; };
  }, []);

  const result = useMemo(() => {
    if (!currentRent || !lastSetDate || !reviewDate) return null;
    return calculateRentReview({
      currentRent,
      lastSetDate,
      reviewDate,
      tenancyStartDate: tenancyStartDate || null,
      cpiPercent: Number(cpiPercent),
      isNewBuildExempt,
      recentRpzArea,
    });
  }, [currentRent, lastSetDate, reviewDate, tenancyStartDate, cpiPercent, isNewBuildExempt, recentRpzArea]);

  // One GA event per successful calculation, so the Ads account can see
  // that this page actually does its job rather than just collecting
  // clicks. Fires on the result transition, not on every keystroke.
  const resultKey = result && result.ok
    ? `${result.maxRent}|${result.increasePercent}|${result.earliestReviewDate}`
    : null;
  useEffect(() => {
    if (!resultKey || !result || !result.ok) return;
    trackEvent('rent_calculator_result', {
      cap_basis: result.capBasis,
      pro_rated: result.isProRated,
      new_build_exempt: result.isNewBuildExempt,
      review_allowed: result.reviewAllowed,
    });
  }, [resultKey]);

  const goSignup = useCallback((placement) => {
    trackEvent('rent_calculator_cta_click', { placement });
    navigate('/signup?utm_source=rent-calculator');
  }, [navigate]);

  const showErrors = touched && result && !result.ok;

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ backgroundColor: BG, color: '#fff', minHeight: '100vh' }}>
        {/* ── Nav (matches LandlordsIreland) ─────────────────── */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'rgba(15,17,23,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: RED_SOLID, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>N</Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.125rem' }}>NestHub</Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button onClick={() => navigate('/login')} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' } }}>Sign in</Button>
              <Button variant="contained" onClick={() => goSignup('nav')} sx={{ backgroundColor: RED_SOLID, '&:hover': { backgroundColor: '#A8232E' }, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', minHeight: 44 }}>
                Start free trial
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* ── Hero ───────────────────────────────────────────── */}
        <Container maxWidth="md" sx={{ textAlign: 'center', pt: { xs: 5, md: 8 }, pb: { xs: 3, md: 4 } }}>
          <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.75rem' }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 2 }}>
            RTB Rent Increase Calculator <Box component="span" sx={{ color: RED }}>(Ireland, 2026)</Box>
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.9375rem', md: '1.0625rem' }, color: 'rgba(255,255,255,0.65)', maxWidth: 640, mx: 'auto' }}>
            Work out your maximum permitted rent under the national rent cap that replaced Rent
            Pressure Zones on 1 March 2026 — plus your earliest review date and your 90-day notice
            date. Free, no signup.
          </Typography>
        </Container>

        {/* ── Calculator ─────────────────────────────────────── */}
        <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 7 } }}>
          <Grid container spacing={3}>
            {/* Inputs */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2.5, md: 3 }, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
                <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.0625rem', mb: 2.5 }}>
                  Your rent review
                </Typography>

                <Stack spacing={2.5}>
                  <TextField
                    label="Current monthly rent (€)"
                    type="number"
                    fullWidth size="small" sx={fieldSx}
                    value={currentRent}
                    onChange={(e) => { setCurrentRent(e.target.value); setTouched(true); }}
                    placeholder="e.g. 1400"
                    inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                    helperText="What you charge today, before the review"
                  />

                  <TextField
                    label="Date rent was last set or reviewed"
                    type="date"
                    fullWidth size="small" sx={fieldSx}
                    value={lastSetDate}
                    onChange={(e) => { setLastSetDate(e.target.value); setTouched(true); }}
                    InputLabelProps={{ shrink: true }}
                    helperText="If the rent has never been reviewed, use the tenancy start date"
                  />

                  <TextField
                    label="Date you want the new rent to start"
                    type="date"
                    fullWidth size="small" sx={fieldSx}
                    value={reviewDate}
                    onChange={(e) => { setReviewDate(e.target.value); setTouched(true); }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Must be at least 90 days after you serve the notice"
                  />

                  <TextField
                    label="Tenancy start date (optional)"
                    type="date"
                    fullWidth size="small" sx={fieldSx}
                    value={tenancyStartDate}
                    onChange={(e) => setTenancyStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Different rules apply before and after 1 March 2026"
                  />

                  <TextField
                    label="CPI — annual inflation (%)"
                    type="number"
                    fullWidth size="small" sx={fieldSx}
                    value={cpiPercent}
                    onChange={(e) => setCpiPercent(e.target.value)}
                    inputProps={{ step: '0.1', inputMode: 'decimal' }}
                    helperText={`Pre-filled with ${DEFAULT_CPI_PERCENT}% (${DEFAULT_CPI_PERIOD}). Edit if a newer figure has been published.`}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: -1.5 }}>
                    Source:{' '}
                    <MuiLink href={DEFAULT_CPI_SOURCE_URL} target="_blank" rel="noopener noreferrer" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      CSO Consumer Price Index
                    </MuiLink>
                    {' · '}
                    <MuiLink href="https://rtb.ie/rtb-rent-calculator/" target="_blank" rel="noopener noreferrer" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      official RTB calculator
                    </MuiLink>
                  </Typography>

                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                  <Stack spacing={0.5}>
                    <FormControlLabel
                      control={<Checkbox checked={isCovered} onChange={(e) => setIsCovered(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: '#FF6B76' }, width: 44, height: 44, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: -2 } }} />}
                      label={<Typography sx={{ fontSize: '0.875rem' }}>This is a private residential tenancy covered by the rent cap</Typography>}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={isNewBuildExempt} onChange={(e) => setIsNewBuildExempt(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: '#FF6B76' }, width: 44, height: 44, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: -2 } }} />}
                      label={<Typography sx={{ fontSize: '0.875rem' }}>New apartment — commencement notice dated on or after 10 June 2025 (CPI only, no 2% ceiling)</Typography>}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={recentRpzArea} onChange={(e) => setRecentRpzArea(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-checked': { color: '#FF6B76' }, width: 44, height: 44, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: -2 } }} />}
                      label={<Typography sx={{ fontSize: '0.875rem' }}>The area only became a Rent Pressure Zone in the last 2 years (24-month review interval)</Typography>}
                    />
                  </Stack>
                </Stack>

                <Box sx={{ mt: 2.5, p: 2, backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <InfoOutlinedIcon sx={{ color: '#60A5FA', fontSize: 18, mt: 0.1 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Rent Pressure Zones no longer exist</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                    RPZs were extended nationwide in June 2025 and abolished on 28 February 2026. Since
                    1 March 2026 one national rent cap applies everywhere, so you no longer need to check
                    whether your address is in a zone.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Results */}
            <Grid item xs={12} md={6}>
              {!isCovered && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  If the arrangement is not a private residential tenancy — for example a licence in
                  your own home, or short-term letting — the national rent cap does not apply and this
                  calculator will not give you a meaningful figure. Check with the RTB.
                </Alert>
              )}

              {showErrors && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <Stack spacing={0.5}>
                    {result.errors.map((e) => (
                      <Typography key={e} sx={{ fontSize: '0.875rem' }}>{e}</Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              {result && result.ok ? (
                <Stack spacing={2}>
                  {/* Headline number */}
                  <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)' }}>
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6EE7B7', letterSpacing: '0.1em', mb: 1 }}>
                      MAXIMUM PERMITTED NEW RENT
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '2.25rem', md: '2.75rem' }, fontWeight: 800, color: '#6EE7B7', lineHeight: 1 }}>
                      {euro(result.maxRent)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', mt: 0.75 }}>
                      per month · up from {euro(result.currentRent)}
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                    <Grid container spacing={2}>
                      {[
                        { label: 'Increase', value: `+${euro(result.increaseAmount)}` },
                        { label: 'Percentage', value: `+${result.increasePercent.toFixed(2)}%` },
                        { label: 'Extra per year', value: `+${euro(result.annualExtra)}` },
                      ].map((s) => (
                        <Grid item xs={4} key={s.label}>
                          <Typography sx={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.5)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {s.label}
                          </Typography>
                          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>{s.value}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  {/* Plain-English rule that produced the number */}
                  <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1 }}>The rule that applies to you</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
                      {result.capExplanation}
                      {result.isProRated && (
                        <>
                          {' '}It has been about {result.monthsSinceLastSet.toFixed(1)} months since the
                          rent was last set — more than 12 — so the annual allowance is applied
                          pro-rata across that period, giving {result.increasePercent.toFixed(2)}% in
                          total.
                        </>
                      )}
                      {!result.isProRated && (
                        <> The review period is 12 months or less, so the full annual allowance applies once.</>
                      )}
                    </Typography>
                    {result.isProRated && (
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: 1.25, lineHeight: 1.6 }}>
                        The RTB does not publish the exact arithmetic it uses for review periods longer
                        than 12 months. We apply the allowance in a straight line (not compounded),
                        which is the more conservative reading — confirm the figure with the official
                        RTB calculator before you serve notice.
                      </Typography>
                    )}
                  </Paper>

                  {/* Warnings — timing problems */}
                  {result.warnings.map((w) => (
                    <Alert key={w} severity="warning" sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}>
                      {w}
                    </Alert>
                  ))}

                  {/* Key dates */}
                  <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                      <EventIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Your dates</Typography>
                    </Box>
                    <Stack spacing={1.5}>
                      {[
                        {
                          label: 'Earliest the rent may be reviewed',
                          value: formatDateIE(result.earliestReviewDate),
                          note: `${result.intervalMonths} months after the rent was last set`,
                        },
                        {
                          label: 'Earliest the new rent can take effect',
                          value: formatDateIE(result.earliestEffectiveDate),
                          note: 'Accounts for both the review interval and the 90-day notice',
                        },
                        // QA #143: never show a serve-by date in the past or an RTB
                        // deadline after the rent takes effect. When the chosen start
                        // date can't be reached, the plan becomes "serve today".
                        result.datesAchievable
                          ? {
                            label: 'Serve the notice on or before',
                            value: formatDateIE(result.latestServeDate),
                            note: `${NOTICE_DAYS} days before your chosen start date`,
                          }
                          : {
                            label: 'Serve the notice',
                            value: 'as soon as possible',
                            warn: true,
                            note: `Your chosen start date can't be reached with ${NOTICE_DAYS} days' notice. Served today, the new rent could take effect on ${formatDateIE(result.recommendedEffectiveDate)}.`,
                          },
                        {
                          label: 'Copy to the RTB by',
                          value: formatDateIE(result.rtbCopyDeadline),
                          warn: !result.datesAchievable,
                          note: `Within ${RTB_COPY_DAYS} days of serving the tenant, or the notice is invalid`,
                        },
                      ].map((d) => (
                        <Box key={d.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                          {d.warn
                            ? <WarningAmberIcon sx={{ color: '#FBBF24', fontSize: 17, mt: 0.3 }} />
                            : <CheckCircleIcon sx={{ color: '#10B981', fontSize: 17, mt: 0.3 }} />}
                          <Box>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                              {d.label}: <Box component="span" sx={{ color: d.warn ? '#FBBF24' : '#6EE7B7' }}>{d.value}</Box>
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>{d.note}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>

                  {/* Tenancy regime branch */}
                  {result.tenancyRegime && (
                    <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1 }}>
                        {result.tenancyRegime.isPostReform
                          ? 'Tenancy created under the 2026 rules'
                          : 'Tenancy created before 1 March 2026'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
                        {result.tenancyRegime.summary}
                      </Typography>
                    </Paper>
                  )}

                  {/* Notice checklist */}
                  <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <GavelIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>What the RTB notice must contain</Typography>
                    </Box>
                    <Stack spacing={1}>
                      {NOTICE_CHECKLIST.map((item) => (
                        <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                          <CheckCircleIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 16, mt: 0.25 }} />
                          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{item}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>

                  {/* Conversion CTA */}
                  <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.35)', textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.125rem', mb: 1 }}>
                      Now generate the actual notice
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.65)', mb: 2.5, lineHeight: 1.6 }}>
                      NestHub fills the RTB rent-review notice from these figures, tracks the 90-day
                      clock and the 7-day RTB copy deadline, and keeps the record for every tenancy.
                    </Typography>
                    <Button
                      variant="contained" size="large" fullWidth endIcon={<ArrowForwardIcon />}
                      onClick={() => goSignup('result')}
                      sx={{ backgroundColor: RED_SOLID, '&:hover': { backgroundColor: '#A8232E' }, textTransform: 'none', fontWeight: 700, py: 1.4 }}
                    >
                      Generate the RTB rent-review notice in NestHub — free 14-day trial
                    </Button>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: 1.5 }}>
                      No credit card required · cancel anytime
                    </Typography>
                  </Paper>
                </Stack>
              ) : (
                !showErrors && (
                  <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.15)' }}>
                    <GavelIcon sx={{ fontSize: 44, color: 'rgba(255,255,255,0.2)', mb: 1.5 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      Fill in the three dates and your current rent
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                      Your maximum permitted rent, earliest review date, 90-day notice date and the
                      RTB notice checklist will appear here.
                    </Typography>
                  </Paper>
                )
              )}
            </Grid>
          </Grid>

          {/* Legal footing */}
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.7 }}>
              This calculator reflects the Residential Tenancies (Miscellaneous Provisions) Act 2026,
              in force from 1 March 2026 (rules checked 16 September 2026). It is a guide, not legal
              advice, and it cannot know every fact about your tenancy. Always confirm your figure
              with the{' '}
              <MuiLink href="https://rtb.ie/rtb-rent-calculator/" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                official RTB Rent Calculator
              </MuiLink>{' '}
              and check{' '}
              <MuiLink href="https://rtb.ie/renting/setting-and-reviewing-private-rents-from-1-march-2026/" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                the RTB&rsquo;s rent-setting guidance
              </MuiLink>{' '}
              before you serve a notice. If your situation involves an exemption, a substantial
              refurbishment or a change of tenancy, check with the RTB.
            </Typography>
          </Alert>
        </Container>

        {/* ── FAQ ────────────────────────────────────────────── */}
        <Box sx={{ py: { xs: 5, md: 7 }, backgroundColor: BG_ALT, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <Container maxWidth="md">
            <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, textAlign: 'center', mb: 4 }}>
              Rent increases in Ireland: the questions landlords ask
            </Typography>
            <Stack spacing={2}>
              {FAQS.map((f) => (
                <Paper key={f.q} sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                  <Typography component="h3" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.9375rem' }}>{f.q}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>{f.a}</Typography>
                </Paper>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ── Final CTA ──────────────────────────────────────── */}
        <Box sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
          <Container maxWidth="md">
            <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, mb: 1.5 }}>
              Stop doing this in a spreadsheet
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3.5, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
              NestHub tracks the rent-review clock for every tenancy you own, generates the RTB
              notice, and tells you before a deadline lapses — not after.
            </Typography>
            <Button
              variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              onClick={() => goSignup('footer')}
              sx={{ backgroundColor: RED_SOLID, '&:hover': { backgroundColor: '#A8232E' }, textTransform: 'none', fontWeight: 700, px: 5, py: 1.5, fontSize: '1rem' }}
            >
              Start your free 14-day trial
            </Button>
            <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', mt: 2 }}>
              Built for Irish landlords · {ANNUAL_CAP_PERCENT}%-or-CPI cap built in · cancel anytime
            </Typography>
          </Container>
        </Box>

        {/* Cold ad traffic bounces here too — same once-per-browser gate. */}
        <ExitIntentFeedback />
      </Box>
    </ThemeProvider>
  );
}
